"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { MapPin, ArrowRight, Users, Calendar as CalendarIcon } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const events = [{
  id: 1,
  title: "Operation: Red Summit",
  date: new Date(2024, 9, 14),
  dateStr: "OCT 14",
  location: "Rugged Ridge Trailhead",
  description: "A 10-mile ruck march to the summit honoring the fallen. Bring your gear and your grit.",
  type: "Ruck March"
}, {
  id: 2,
  title: "Basecamp Fire Talk",
  date: new Date(2024, 9, 28),
  dateStr: "OCT 28",
  location: "Paniolo HQ, Rugged Ridge",
  description: "Open community discussion night. Coffee and fire provided. Come ready to listen or speak.",
  type: "Community"
}, {
  id: 3,
  title: "Veterans Day Vigil",
  date: new Date(2024, 10, 11),
  dateStr: "NOV 11",
  location: "Memorial Park",
  description: "Standing watch from sunrise to sunset. Join us in paying respects to those who served.",
  type: "Ceremony"
}, {
  id: 4,
  title: "Winter Survival Workshop",
  date: new Date(2024, 11, 7),
  dateStr: "DEC 07",
  location: "North Woods Training Ground",
  description: "Learn essential cold-weather survival skills with our expert instructors.",
  type: "Training"
}];
export default function CommunityRallyPoint() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const eventDates = events.map(e => e.date);
  return <section id="community-rally-point" className="dark bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--color-primary)_0%,_transparent_35%)] opacity-10"></div>
        {}
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570849/img/topography_pattern_dark.png')] opacity-10 bg-repeat bg-[length:400px_400px] mix-blend-overlay"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {}
          <div className="lg:col-span-5 flex flex-col gap-8">
            <div ref={ref} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="border-primary/50 text-primary uppercase tracking-widest text-[10px]">
                  Incoming Intel
                </Badge>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 text-white">
                THE RALLY POINT
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                We don't just sell gear; we build brotherhood. Check our operational timeline for upcoming meetups, rucks, and community events.
              </p>

              {}
              <div className="hidden lg:block p-6 rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <div className="flex items-center gap-2 mb-4 text-sm font-medium text-muted-foreground">
                  <SafeHydrate><CalendarIcon className="w-4 h-4 text-primary" /></SafeHydrate>
                  <span>Operational Calendar</span>
                </div>
                <SafeHydrate><Calendar mode="multiple" selected={eventDates} className="rounded-md border border-border/50 bg-background/50 w-full flex justify-center pointer-events-none" /></SafeHydrate>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="relative pl-8 border-l border-border/30 space-y-12">
              {events.map((event, index) => <div key={event.id} data-index={index} className={`relative transition-all duration-700 delay-${index * 100} ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
                  {}
                  <div className="absolute -left-[39px] top-0 flex items-center justify-center w-5 h-5 rounded-full bg-background border-2 border-primary shadow-[0_0_10px_rgba(220,38,38,0.5)]">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    {}
                    <div className="shrink-0">
                      <div className="flex flex-col items-center justify-center w-16 h-16 rounded-md bg-primary text-primary-foreground shadow-lg border border-primary/50">
                        <span className="text-[10px] font-medium uppercase opacity-90">{event.dateStr.split(' ')[0]}</span>
                        <span className="text-2xl font-bold leading-none">{event.dateStr.split(' ')[1]}</span>
                      </div>
                    </div>

                    {}
                    <div className="flex-1 space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-2xl font-bold text-white">{event.title}</h3>
                        <Badge variant="secondary" className="text-[10px] uppercase tracking-wider bg-secondary/50 border border-border/50">
                          {event.type}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-primary/90 font-medium">
                        <MapPin className="w-4 h-4" />
                        {event.location}
                      </div>
                      
                      <p className="text-muted-foreground leading-relaxed">
                        {event.description}
                      </p>

                      <div className="pt-2">
                        <Button asChild variant="outline" className="group border-primary/30 hover:border-primary hover:bg-primary/10 text-foreground">
                          <Link to="/TheRallyPointPage">
                            Join Operation
                            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>

            {}
            <div className={`mt-12 pl-8 transition-all duration-700 delay-500 ${isVisible ? "opacity-100" : "opacity-0"}`}>
              <Card className="bg-secondary/20 border-border/50 backdrop-blur-sm">
                <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-primary/10 text-primary">
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">Host Your Own Meetup</h4>
                      <p className="text-sm text-muted-foreground">Got a squad? Organize a local ruck or event.</p>
                    </div>
                  </div>
                  <Button asChild>
                    <Link to="/ContactHQPage">
                      Request Support
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>

        </div>
      </div>
    </section>;
}